import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Descriptions, Drawer, Dropdown, Form, Grid, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, EditOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { get, post, patch } from '../../lib/request'
import {
  ANCHOR_STATUS_LABELS,
  ASSIGN_TYPE_LABELS,
  AnchorPlanSnapshot,
  AnchorTierRule,
  BENEFIT_CONTENT_SOURCE_LABELS,
  BENEFIT_DELIVERY_METHOD_LABELS,
  BusinessFormat,
  FollowUp,
  IDENTITY_LABELS,
  INTENT_CATEGORY_LABELS,
  LEAD_STATUS_LABELS,
  LeadAnchorView,
  LeadDetail,
  LeadItem,
  PageData,
  SOURCE_MODE_LABELS,
  VERIFY_STATUS_LABELS,
  downloadCsv,
  errMsg,
  fmtDate,
  fmtMoney,
  fmtTime,
} from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'
import FollowTimeline from '../../components/FollowTimeline'
import LeadStatusActions from '../../components/LeadStatusActions'
import type { ProjectItem } from '../Projects/index'

const { Text } = Typography

/**
 * reward-anchor-confirm-v1（ST3）：锚定快照分档行的数值清洗（后端 Json 下发，可能为字符串）。
 * 非有限数值一律 null（渲染 '—' / '无上限'），不做任何写操作。
 */
const anchorNum = (v: unknown): number | null => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** 锚定快照方案正文段落：跳过 null/空串（调用方全空时显示 '—'）；label 口径同 BenefitConfig/PlanTab */
const anchorPlanParagraphs = (snap: AnchorPlanSnapshot): { label: string; text: string }[] => {
  const rows: { label: string; text?: string | null }[] = [
    { label: '推荐人答谢·现金', text: snap.cashRewardText },
    { label: '推荐人答谢·实物', text: snap.inKindText },
    { label: '被推荐人礼遇', text: snap.referredBenefitText },
  ]
  return rows
    .filter((r) => typeof r.text === 'string' && r.text.trim() !== '')
    .map((r) => ({ label: r.label, text: r.text as string }))
}

/** 分档表列（只读，无任何编辑控件）：字段口径镜像 BenefitConfig/TierTable（左闭右开，空=无上限） */
const ANCHOR_TIER_COLUMNS: ColumnsType<AnchorTierRule> = [
  {
    title: '面积区间(㎡)',
    key: 'range',
    width: 150,
    render: (_: unknown, r) => {
      const to = anchorNum(r.areaTo)
      return `${anchorNum(r.areaFrom) ?? 0} ~ ${to == null ? '无上限' : to}`
    },
  },
  { title: '礼遇内容', dataIndex: 'benefitContent', key: 'benefitContent', render: (v?: string | null) => v ?? '—' },
  {
    title: '券面值',
    dataIndex: 'couponValue',
    key: 'couponValue',
    width: 110,
    render: (v: unknown) => {
      const n = anchorNum(v)
      return n == null ? '—' : n.toLocaleString('zh-CN')
    },
  },
]

/**
 * reward-anchor-confirm-v1（ST3）：线索详情「奖励方案锚定」只读区块。
 * 只读 by construction：仅 Descriptions/Tag/Table 展示，无 Form/Input/onChange/编辑按钮，
 * 亦不挂 canEdit 分支——可见性沿用后端 GET /admin/leads/:id 的既有角色收窄。
 */
const AnchorReadonlyBlock: React.FC<{ anchor: LeadAnchorView }> = ({ anchor }) => {
  const snap: AnchorPlanSnapshot = anchor.planSnapshot ?? {}
  const statusMeta = ANCHOR_STATUS_LABELS[anchor.status]
  const tiers = Array.isArray(snap.tierRulesJson)
    ? snap.tierRulesJson.filter((t): t is AnchorTierRule => t != null && typeof t === 'object')
    : []
  const paragraphs = anchorPlanParagraphs(snap)
  const protectText = anchor.protectUntil
    ? `${fmtDate(anchor.visitAt)} → ${fmtDate(anchor.protectUntil)}${
        anchor.protectionDays != null ? `（${anchor.protectionDays} 天）` : ''
      }`
    : '—'
  return (
    <div style={{ marginBottom: 12 }}>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="状态">
          <Tag color={statusMeta?.color ?? 'default'}>{statusMeta?.label ?? anchor.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="客户称呼">{anchor.customerNameMasked || '—'}</Descriptions.Item>
        <Descriptions.Item label="到访时间">{fmtTime(anchor.visitAt)}</Descriptions.Item>
        <Descriptions.Item label="保护期">{protectText}</Descriptions.Item>
        <Descriptions.Item label="业态">
          {anchor.businessFormat ? INTENT_CATEGORY_LABELS[anchor.businessFormat] ?? anchor.businessFormat : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="答谢类型">{anchor.rewardTypeCode ?? snap.rewardTypeCode ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="方案名">{snap.benefitPlanName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="兑付方式">
          {snap.benefitDeliveryMethod
            ? BENEFIT_DELIVERY_METHOD_LABELS[snap.benefitDeliveryMethod] ?? snap.benefitDeliveryMethod
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="内容来源">
          {snap.benefitContentSource
            ? BENEFIT_CONTENT_SOURCE_LABELS[snap.benefitContentSource] ?? snap.benefitContentSource
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="方案正文">
          {paragraphs.length > 0 ? (
            paragraphs.map((p) => (
              <div key={p.label} style={{ fontSize: 13 }}>
                {p.label}：{p.text}
              </div>
            ))
          ) : (
            <span style={{ color: '#98A2B3' }}>—</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="客户确认时间">{fmtTime(anchor.confirmedAt)}</Descriptions.Item>
      </Descriptions>
      {tiers.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '8px 0 4px' }}>
            面积分档（{tiers.length} 档）
          </Text>
          <Table<AnchorTierRule>
            rowKey={(_r, idx) => `anchor-tier-${idx ?? 0}`}
            size="small"
            columns={ANCHOR_TIER_COLUMNS}
            dataSource={tiers}
            pagination={false}
          />
        </>
      )}
    </div>
  )
}

/**
 * 全部线索（full_feature_v3）
 * 全字段列表 + 高级筛选 + 搜索 + 导出 CSV + 详情抽屉（时间轴）+ SOP 操作按钮组
 *
 * thankyou_v1（批次 2）：成交录入/编辑弹窗增「成交业态」下拉（业态表启用项，可清空）；
 * 列表在「意向业态」后增「成交业态」列；详情抽屉成交块增一行。
 * INTENT_CATEGORY_LABELS 已提升至 lib/api.ts 共享导出（code→中文内置兜底），本文件改 import。
 *
 * reward-anchor-confirm-v1（ST3）：详情抽屉「带看记录」后增只读「奖励方案锚定」区块
 * （AnchorReadonlyBlock）——未锚定灰字「未锚定」，有锚定则 Descriptions column=1 展示状态/
 * 客户称呼/到访时间/保护期/业态/答谢类型/方案名/兑付方式/内容来源/方案正文/分档表/客户确认时间。
 * 零编辑入口（不挂 canEdit 分支），可见性沿用后端详情接口的既有角色收窄。
 */
const LeadsPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canTransfer = hasPermission(role, 'LEAD_TRANSFER')
  const canClaim = hasPermission(role, 'LEAD_CLAIM')
  const canEdit = hasPermission(role, 'LEAD_EDIT')

  // leads-mobile-fix：手机端（<768px）操作列取消右侧固定并收窄为菜单化渲染，避免遮挡左侧线索ID列；桌面端零变化。
  // 断点判断模式与 AdminLayout.tsx 保持一致（Grid.useBreakpoint + !screens.md）。
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const [list, setList] = useState<LeadItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [verificationStatus, setVerificationStatus] = useState<string | undefined>(undefined)
  const [keyword, setKeyword] = useState('')
  const [projectId, setProjectId] = useState<number | undefined>(undefined)
  const [projectOptions, setProjectOptions] = useState<{ value: number; label: string }[]>([])

  const [detail, setDetail] = useState<LeadDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [timeline, setTimeline] = useState<FollowUp[]>([])
  const [exporting, setExporting] = useState(false)

  
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editRecord, setEditRecord] = useState<LeadItem | null>(null)
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(false)

  // 成交信息编辑
  const [dealEditOpen, setDealEditOpen] = useState(false)
  const [dealForm] = Form.useForm()
  const [editingDeal, setEditingDeal] = useState(false)

  // thankyou_v1（批次 2）：业态表（成交业态下拉选项 = published 项；列表 code→中文优先取动态名称）
  const [businessFormats, setBusinessFormats] = useState<BusinessFormat[]>([])

  // 跟进记录编辑
  const [followupEditOpen, setFollowupEditOpen] = useState(false)
  const [followupEditRecord, setFollowupEditRecord] = useState<FollowUp | null>(null)
  const [followupEditForm] = Form.useForm()
  const [editingFollowup, setEditingFollowup] = useState(false)

const queryParams = useCallback(
    () => ({
      page,
      pageSize,
      ...(status ? { status } : {}),
      ...(verificationStatus ? { verificationStatus } : {}),
      ...(keyword ? { keyword } : {}),
      ...(projectId ? { projectId } : {}),
    }),
    [page, pageSize, status, verificationStatus, keyword, projectId],
  )

  const fetchList = useCallback(() => {
    setLoading(true)
    get<PageData<LeadItem>>('/admin/leads', { params: queryParams() })
      .then((d) => {
        setList(d.items ?? [])
        setTotal(d.total ?? 0)
      })
      .catch((err) => {
        message.warning(errMsg(err, '线索列表加载失败（后端可能未启动）'))
        setList([])
      })
      .finally(() => setLoading(false))
  }, [queryParams])

  useEffect(() => { fetchList() }, [fetchList])

  // multi_project_display_v1：意向项目筛选下拉选项（取启用项目列表）
  useEffect(() => {
    get<ProjectItem[]>('/admin/projects')
      .then((d) => {
        const arr = Array.isArray(d) ? d : []
        setProjectOptions(arr.map((p) => ({ value: p.id, label: p.name })))
      })
      .catch(() => setProjectOptions([]))
  }, [])

  // thankyou_v1（批次 2）：业态表（后台可新增业态，故列表/详情展示优先用动态 name）
  useEffect(() => {
    get<BusinessFormat[]>('/admin/business-formats')
      .then((d) => setBusinessFormats(Array.isArray(d) ? d : []))
      // 读失败不阻断本页：成交业态列降级到共享内置映射兜底
      .catch(() => setBusinessFormats([]))
  }, [])

  /** 成交业态 code → 中文：动态业态表 name 优先，共享内置映射 INTENT_CATEGORY_LABELS 兜底 */
  const dealCategoryLabel = (code?: string | null): string => {
    if (!code) return '—'
    return businessFormats.find((f) => f.code === code)?.name ?? INTENT_CATEGORY_LABELS[code] ?? code
  }

  /** 成交业态下拉选项（值 = code；仅已发布项，后端亦 400 拦非启用 code） */
  const dealCategoryOptions = businessFormats
    .filter((f) => f.status === 'published')
    .map((f) => ({ value: f.code, label: f.name }))

  const openDetail = (record: LeadItem) => {
    // 列表行的 deal 是精简子集（仅 id/到账/首月租金/签约日），与详情的完整 DealInfo 不兼容。
    // 占位阶段置空，等下方详情接口返回后由 setDetail 覆盖为完整对象。
    setDetail({ ...record, deal: undefined })
    setDetailOpen(true)
    setTimeline([])
    get<LeadDetail>(`/admin/leads/${record.id}`)
      .then(setDetail)
      .catch(() => message.warning('线索详情加载失败，展示列表数据'))
    get<FollowUp[]>(`/admin/leads/${record.id}/timeline`)
      .then((d) => setTimeline(Array.isArray(d) ? d : []))
      .catch(() => setTimeline([]))
  }

  const refreshDetail = () => {
    if (detail) {
      openDetail({ ...detail })
    }
    fetchList()
  }

  const handleExport = () => {
    setExporting(true)
    downloadCsv('/admin/leads/export', 'leads.csv', queryParams())
      .catch((err: unknown) => message.error(errMsg(err, '导出失败')))
      .finally(() => setExporting(false))
  }

  const handleClaim = (record: LeadItem) => {
    Modal.confirm({
      title: '认领公海线索',
      content: `确认认领「${record.name ?? record.phone}」为自己的跟进线索？`,
      okText: '确认认领',
      cancelText: '取消',
      onOk: () =>
        post('/admin/leads/claim', { leadId: record.id })
          .then(() => {
            message.success('认领成功')
            fetchList()
          })
          .catch((err: unknown) => message.error(errMsg(err, '认领失败'))),
    })
  }


  const handleCreate = () => {
    setCreating(true)
    createForm.validateFields().then((values) => {
      post('/admin/leads', values)
        .then(() => {
          message.success('线索创建成功')
          setCreateOpen(false)
          createForm.resetFields()
          fetchList()
        })
        .catch((err) => message.error(errMsg(err, '创建失败')))
        .finally(() => setCreating(false))
    }).catch(() => setCreating(false))
  }

  const handleEdit = (record: LeadItem) => {
    setEditRecord(record)
    editForm.setFieldsValue({
      phone: record.phone,
      name: record.name,
      company: record.company,
      notes: record.notes,
    })
    setEditOpen(true)
  }

  const handleEditSubmit = () => {
    if (!editRecord) return
    setEditing(true)
    editForm.validateFields().then((values) => {
      const url = '/admin/leads/' + editRecord.id
      patch(url, values)
        .then(() => {
          message.success('线索更新成功')
          setEditOpen(false)
          fetchList()
        })
        .catch((err) => message.error(err.message || '更新失败'))
        .finally(() => setEditing(false))
    }).catch(() => setEditing(false))
  }

  const handleDealEdit = (record: LeadDetail) => {
    if (!record.deal) return
    dealForm.setFieldsValue({
      unit: record.deal.unit,
      area: record.deal.area,
      unitPrice: record.deal.unitPrice,
      leaseMonths: record.deal.leaseMonths,
      signedAt: record.deal.signedAt,
      // thankyou_v1（批次 2）：回填成交业态（code）
      dealCategory: record.deal.dealCategory ?? undefined,
    })
    setDealEditOpen(true)
  }

  const handleDealEditSubmit = () => {
    if (!detail) return
    setEditingDeal(true)
    dealForm.validateFields().then((values) => {
      patch('/admin/leads/' + detail.id + '/deal', {
        ...values,
        // allowClear 清空时为 undefined → 传 null 显式清空（后端 null/'' 均置 null）
        dealCategory: values.dealCategory ?? null,
      })
        .then(() => {
          message.success('成交信息更新成功')
          setDealEditOpen(false)
          refreshDetail()
        })
        .catch((err) => message.error(errMsg(err, '更新失败')))
        .finally(() => setEditingDeal(false))
    }).catch(() => setEditingDeal(false))
  }

  const handleFollowupEdit = (followup: FollowUp) => {
    setFollowupEditRecord(followup)
    followupEditForm.setFieldsValue({
      content: followup.content,
      nextContactAt: followup.nextContactAt,
      tourAt: followup.tourAt,
      tourUnit: followup.tourUnit,
      tourFeedback: followup.tourFeedback,
    })
    setFollowupEditOpen(true)
  }

  const handleFollowupEditSubmit = () => {
    if (!detail || !followupEditRecord) return
    setEditingFollowup(true)
    followupEditForm.validateFields().then((values) => {
      patch('/admin/leads/' + detail.id + '/followups/' + followupEditRecord.id, values)
        .then(() => {
          message.success('跟进记录更新成功')
          setFollowupEditOpen(false)
          refreshDetail()
        })
        .catch((err) => message.error(err.message || '更新失败'))
        .finally(() => setEditingFollowup(false))
    }).catch(() => setEditingFollowup(false))
  }

  const columns: ColumnsType<LeadItem> = [
    { title: '线索ID', dataIndex: 'id', key: 'id', width: 80, render: (v: number) => `#${v}` },
    { title: '看房人', dataIndex: 'name', key: 'name' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '公司', dataIndex: 'company', key: 'company', ellipsis: true, render: (v?: string | null) => v ?? '—' },
    {
      title: '意向面积/行业',
      key: 'intent',
      render: (_: unknown, r) => (
        <Text style={{ fontSize: 12 }}>
          {r.areaIntent != null ? `${r.areaIntent}㎡` : '—'} {r.industry ? `· ${r.industry}` : ''}
        </Text>
      ),
    },
    {
      title: '意向项目',
      key: 'project',
      width: 130,
      ellipsis: true,
      render: (_: unknown, r) =>
        r.project?.name ? <Text>{r.project.name}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: '意向业态',
      key: 'intentCategory',
      width: 90,
      render: (_: unknown, r) =>
        r.intentCategory ? (
          <Text>{INTENT_CATEGORY_LABELS[r.intentCategory] ?? r.intentCategory}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    // thankyou_v1（批次 2）：成交业态（code→中文：动态业态表 name 优先，内置映射兜底）
    {
      title: '成交业态',
      key: 'dealCategory',
      width: 90,
      render: (_: unknown, r) =>
        r.deal?.dealCategory ? (
          <Text>{dealCategoryLabel(r.deal.dealCategory)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '来源',
      dataIndex: 'sourceMode',
      key: 'sourceMode',
      width: 110,
      render: (v: string) => <Tag>{SOURCE_MODE_LABELS[v] ?? v}</Tag>,
    },
    {
      title: '归属推荐人',
      key: 'referrer',
      render: (_: unknown, r) =>
        r.referrer ? (
          <span>
            {r.referrer.name}
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>({r.referrer.rid})</Text>
          </span>
        ) : (
          <Tag color="cyan">自然到访</Tag>
        ),
    },
    {
      title: '校验状态',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      width: 96,
      render: (v: string) => (
        <Tag color={VERIFY_STATUS_LABELS[v]?.color ?? 'default'}>{VERIFY_STATUS_LABELS[v]?.label ?? v}</Tag>
      ),
    },
    {
      title: '跟进状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: string) => (
        <Tag color={LEAD_STATUS_LABELS[v as keyof typeof LEAD_STATUS_LABELS]?.color ?? 'default'}>
          {LEAD_STATUS_LABELS[v as keyof typeof LEAD_STATUS_LABELS]?.label ?? v}
        </Tag>
      ),
    },
    {
      title: '保护期至',
      dataIndex: 'protectUntil',
      key: 'protectUntil',
      width: 110,
      render: (v?: string | null, r?: LeadItem) =>
        v ? (
          <Text type={r?.attributionStatus === 'released' ? 'secondary' : undefined} style={{ fontSize: 12 }}>
            {fmtDate(v)}
            {r?.attributionStatus === 'released' && '（已释放）'}
          </Text>
        ) : (
          '—'
        ),
    },
    {
      title: '跟进人',
      key: 'assignee',
      width: 120,
      render: (_: unknown, r) =>
        r.assigneeId ? (
          <span>
            {r.assigneeName ?? `#${r.assigneeId}`}
            {r.assignmentType && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                （{ASSIGN_TYPE_LABELS[r.assignmentType] ?? r.assignmentType}）
              </Text>
            )}
          </span>
        ) : (
          <Tag color="warning">公海</Tag>
        ),
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 150, render: fmtTime },
    {
      title: '操作',
      key: 'actions',
      fixed: isMobile ? false : ('right' as const),
      width: isMobile ? 200 : 280,
      render: (_: unknown, r) =>
        isMobile ? (
          // leads-mobile-fix：手机端仅「详情」平铺，其余动作收进 ⋯ 菜单（权限过滤逻辑不变）
          <Space size={4} wrap>
            <Button size="small" onClick={() => openDetail(r)}>
              详情
            </Button>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  ...(canEdit ? [{ key: 'edit', icon: <EditOutlined />, label: '编辑' }] : []),
                  ...(canClaim && !r.assigneeId ? [{ key: 'claim', label: '认领' }] : []),
                ],
                onClick: ({ key }) => {
                  if (key === 'edit') handleEdit(r)
                  else if (key === 'claim') handleClaim(r)
                },
              }}
            >
              <Button size="small" icon={<MoreOutlined />} aria-label="更多操作" />
            </Dropdown>
            <LeadStatusActions lead={r} canTransfer={canTransfer} onDone={fetchList} mobile />
          </Space>
        ) : (
          <Space size={4} wrap>
            <Button size="small" onClick={() => openDetail(r)}>
              详情
            </Button>
            {canEdit && (
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>
                编辑
              </Button>
            )}
            {canClaim && !r.assigneeId && (
              <Button
                size="small"
                type="primary"
                style={{ background: '#F39C12', borderColor: '#F39C12' }}
                onClick={() => handleClaim(r)}
              >
                认领
              </Button>
            )}
            <LeadStatusActions lead={r} canTransfer={canTransfer} onDone={fetchList} />
          </Space>
        ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 14 }} wrap>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新建线索
        </Button>
<Input.Search
          allowClear
          placeholder="搜索姓名 / 手机号 / 公司"
          style={{ width: 260 }}
          onSearch={(v) => { setKeyword(v); setPage(1) }}
        />
        <Select
          allowClear
          placeholder="跟进状态"
          style={{ minWidth: 130 }}
          value={status}
          onChange={(v) => { setStatus(v); setPage(1) }}
          options={Object.entries(LEAD_STATUS_LABELS).map(([value, s]) => ({ value, label: s.label }))}
        />
        <Select
          allowClear
          placeholder="校验状态"
          style={{ minWidth: 130 }}
          value={verificationStatus}
          onChange={(v) => { setVerificationStatus(v); setPage(1) }}
          options={Object.entries(VERIFY_STATUS_LABELS).map(([value, s]) => ({ value, label: s.label }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="意向项目"
          style={{ minWidth: 160 }}
          value={projectId}
          onChange={(v) => { setProjectId(v); setPage(1) }}
          options={projectOptions}
          optionFilterProp="label"
        />
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={handleExport}
          style={{ minHeight: 44 }}
        >
          导出 CSV（含当前筛选）
        </Button>
      </Space>
      <Table<LeadItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) },
        }}
      />

      <Drawer
        title={`线索详情 · ${detail?.name ?? ''}（${detail?.phone ?? ''}）`}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={Math.min(640, window.innerWidth)}
      >
        {detail && (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="公司">{detail.company ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="意向面积 / 行业">
                {detail.areaIntent != null ? `${detail.areaIntent}㎡` : '—'} · {detail.industry ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="来源">{SOURCE_MODE_LABELS[detail.sourceMode] ?? detail.sourceMode}</Descriptions.Item>
              {/* lead_form_slim：预计到访时间——四档文案优先，回退旧版 ISO 详细时间（fmtTime null→'—'） */}
              <Descriptions.Item label="预计到访时间">{detail.visitTimeText ?? fmtTime(detail.visitTime)}</Descriptions.Item>
              {/* lead_gender_v1：客户性别（male→男 / female→女 / 其他与空→—），只读展示，不入编辑表单 */}
              <Descriptions.Item label="性别">{detail.gender === 'male' ? '男' : detail.gender === 'female' ? '女' : '—'}</Descriptions.Item>
              <Descriptions.Item label="意向项目">{detail.project?.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="意向业态">
                {detail.intentCategory ? INTENT_CATEGORY_LABELS[detail.intentCategory] ?? detail.intentCategory : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="归属推荐人">
                {detail.referrer
                  ? `${detail.referrer.name}（${[detail.referrer.rid, IDENTITY_LABELS[(detail.referrer as { identity?: string }).identity ?? '']].filter(Boolean).join('，')}）`
                  : <Tag color="cyan">自然到访</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="保护期">
                {detail.protectUntil ? fmtDate(detail.protectUntil) : '—'}
                {detail.protectionDaysSnapshot != null && `（快照 ${detail.protectionDaysSnapshot} 天）`}
                {detail.attributionStatus === 'released' && <Tag style={{ marginLeft: 8 }}>归属已释放</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={LEAD_STATUS_LABELS[detail.status]?.color ?? 'default'}>
                  {LEAD_STATUS_LABELS[detail.status]?.label ?? detail.status}
                </Tag>
                <Tag color={VERIFY_STATUS_LABELS[detail.verificationStatus]?.color ?? 'default'}>
                  {VERIFY_STATUS_LABELS[detail.verificationStatus]?.label ?? detail.verificationStatus}
                </Tag>
                {detail.verifiedAt && <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(detail.verifiedAt)} 核验</Text>}
              </Descriptions.Item>
              {detail.invalidReason && (
                <Descriptions.Item label="无效原因">{detail.invalidReason}</Descriptions.Item>
              )}
              <Descriptions.Item label="跟进人">
                {detail.assigneeId
                  ? `${detail.assigneeName ?? `#${detail.assigneeId}`}（${ASSIGN_TYPE_LABELS[detail.assignmentType ?? ''] ?? '—'}）`
                  : '公海'}
              </Descriptions.Item>
              {detail.deal && (
                <Descriptions.Item label="成交信息">
                  {detail.deal.unitRef
                    ? `${detail.deal.unitRef.building} ${detail.deal.unitRef.floor}层 ${detail.deal.unitRef.unitNo}`
                    : detail.deal.unit ?? '—'}
                  {detail.deal.area != null && ` · ${detail.deal.area}㎡`}
                  {detail.deal.unitPrice != null && ` × ${detail.deal.unitPrice}元/㎡/月`}
                  {detail.deal.firstMonthRent != null && ` · 首月租金 ${fmtMoney(detail.deal.firstMonthRent)}`}
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    签约 {fmtDate(detail.deal.signedAt)} · 两押一租到账 {fmtDate(detail.deal.paymentCollectedAt)}
                  </Text>
                  {/* thankyou_v1（批次 2）：成交业态 */}
                  <br />
                  <Text style={{ fontSize: 12 }}>
                    成交业态：{dealCategoryLabel(detail.deal.dealCategory)}
                  </Text>
                {canEdit && <Button size="small" icon={<EditOutlined />} onClick={() => handleDealEdit(detail!)} style={{ marginLeft: 8 }}>编辑</Button>}</Descriptions.Item>
              )}
              {detail.notes && <Descriptions.Item label="备注">{detail.notes}</Descriptions.Item>}
            </Descriptions>

            <div style={{ margin: '12px 0' }}>
              <LeadStatusActions lead={detail} canTransfer={canTransfer} onDone={refreshDetail} />
            </div>

            {/* tour_multi_v1：带看台账（lead_tours 倒序，多次带看留痕） */}
            <h4 style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 8 }}>
              带看记录（{detail.tourCount ?? detail.tourList?.length ?? 0} 次）
            </h4>
            {detail.tourList && detail.tourList.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                {detail.tourList.map((tour, idx) => (
                  <div key={tour.id} style={{ padding: '6px 0', borderBottom: '1px dashed #EEF1F4', fontSize: 13 }}>
                    <Text strong style={{ marginRight: 8 }}>第 {detail.tourList!.length - idx} 次</Text>
                    <Text type="secondary">{fmtTime(tour.tourAt)}{tour.operatorName ? ` · 登记：${tour.operatorName}` : ''}</Text>
                    {tour.tourUnit && <div style={{ marginTop: 2 }}>单元：{tour.tourUnit}</div>}
                    {tour.tourFeedback && <div style={{ marginTop: 2 }}>反馈：{tour.tourFeedback}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#98A2B3' }}>暂无带看记录</div>
            )}

            {/* reward-anchor-confirm-v1（ST3）：奖励方案锚定（只读；未锚定灰字提示） */}
            <h4 style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 8 }}>奖励方案锚定</h4>
            {detail.anchor ? (
              <AnchorReadonlyBlock anchor={detail.anchor} />
            ) : (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#98A2B3' }}>未锚定</div>
            )}

            <h4 style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 8 }}>全生命周期时间轴</h4>
            <FollowTimeline items={timeline.length > 0 ? timeline : detail.followUps ?? []} onEdit={canEdit ? handleFollowupEdit : undefined} />
          </>
        )}
      </Drawer>
    
      <Modal
        title="新建线索"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields() }}
        onOk={handleCreate}
        confirmLoading={creating}
        width={520}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="company" label="公司">
            <Input placeholder="请输入公司（选填）" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注（选填）" />
          </Form.Item>
          <Form.Item
            name="rid"
            label="归属推荐人推荐码"
            extra="选填，输入推荐人推荐码可将该线索绑定到对应推荐人；推荐码无效时提交会提示"
          >
            <Input placeholder="选填，输入推荐人推荐码可绑定归属" allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑线索"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleEditSubmit}
        confirmLoading={editing}
        width={520}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="company" label="公司">
            <Input placeholder="请输入公司（选填）" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注（选填）" />
          </Form.Item>
        </Form>
      </Modal>


      <Modal
        title="编辑成交信息"
        open={dealEditOpen}
        onCancel={() => setDealEditOpen(false)}
        onOk={handleDealEditSubmit}
        confirmLoading={editingDeal}
        destroyOnClose
      >
        <Form form={dealForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="unit" label="房号" rules={[{ required: true, message: "请输入房号" }]}>
            <Input placeholder="请输入房号" />
          </Form.Item>
          <Form.Item name="area" label="面积(㎡)" rules={[{ required: true, message: "请输入面积" }]}>
            <InputNumber style={{ width: "100%" }} placeholder="请输入面积" />
          </Form.Item>
          <Form.Item name="unitPrice" label="单价(元/㎡/月)" rules={[{ required: true, message: "请输入单价" }]}>
            <InputNumber style={{ width: "100%" }} placeholder="请输入单价" />
          </Form.Item>
          <Form.Item name="leaseMonths" label="租期(月)" rules={[{ required: true, message: "请输入租期" }]}>
            <InputNumber style={{ width: "100%" }} placeholder="请输入租期" />
          </Form.Item>
          <Form.Item name="signedAt" label="签约日期" rules={[{ required: true, message: "请选择签约日期" }]}>
            <Input type="date" placeholder="请选择签约日期" />
          </Form.Item>
          {/* thankyou_v1（批次 2）：成交业态（选项自动取自业态表启用项，值=code，可清空） */}
          <Form.Item name="dealCategory" label="成交业态（可清空）">
            <Select
              allowClear
              placeholder="选择成交业态（可选）"
              style={{ minHeight: 44 }}
              options={dealCategoryOptions}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑跟进记录"
        open={followupEditOpen}
        onCancel={() => { setFollowupEditOpen(false); setFollowupEditRecord(null) }}
        onOk={handleFollowupEditSubmit}
        confirmLoading={editingFollowup}
        destroyOnClose
      >
        <Form form={followupEditForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="content" label="沟通内容">
            <Input.TextArea rows={3} placeholder="请输入沟通内容" />
          </Form.Item>
          <Form.Item name="nextContactAt" label="下次联系时间">
            <Input type="datetime-local" />
          </Form.Item>
          {followupEditRecord?.type === "tour" && (
            <>
              <Form.Item name="tourAt" label="带看时间">
                <Input type="datetime-local" />
              </Form.Item>
              <Form.Item name="tourUnit" label="带看单元">
                <Input placeholder="请输入带看单元" />
              </Form.Item>
              <Form.Item name="tourFeedback" label="带看反馈">
                <Input.TextArea rows={2} placeholder="请输入带看反馈" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

    </>
  )
}

export default LeadsPage
